import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Filter, X } from 'lucide-react'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { cn } from '@lib/utils/helpers'
import styles from './listFilterDrawer.module.css'
import type { ListFilterDrawerProps } from './ListFilterDrawer.types'

/**
 * Shared right-side filter drawer shell used by listing pages.
 * Keeps overlay behavior, escape handling, and Invitee-style footer actions consistent.
 */
export function ListFilterDrawer({
  isOpen,
  onClose,
  onReset,
  onDone,
  children,
  title = 'Filters',
  doneLabel = 'Done',
  resetLabel = 'Reset All',
  testIdPrefix = 'list',
  className,
}: ListFilterDrawerProps) {
  useBodyScrollLock(isOpen)

  useEffect(() => {
    if (!isOpen) return
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  if (typeof document === 'undefined') return null

  const content = (
    <>
      <div
        data-testid={`${testIdPrefix}-filters-drawer-backdrop`}
        className={cn(
          'fixed inset-0 z-[2147483646] h-[100dvh] bg-black/20 backdrop-blur-sm transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onClose}
      />
      <aside
        data-testid={`${testIdPrefix}-filters-drawer`}
        className={cn(
          'touch-scroll fixed inset-y-0 right-0 z-[2147483647] flex h-[100dvh] max-h-[100dvh] w-full flex-col overflow-y-auto bg-white shadow-2xl transition-transform duration-300 ease-in-out sm:max-w-sm',
          isOpen ? 'translate-x-0' : 'translate-x-full',
          className,
        )}
        aria-hidden={!isOpen}
      >
        <div
          data-testid={`${testIdPrefix}-filters-drawer-header`}
          className="flex items-center justify-between border-b border-slate-100 px-6 py-4"
        >
          <div className="flex items-center gap-2 text-slate-800">
            <Filter className="h-5 w-5 text-[rgb(21_151_255)]" />
            <h2
              data-testid={`${testIdPrefix}-filters-drawer-title`}
              className="text-lg font-bold"
            >
              {title}
            </h2>
          </div>
          <button
            type="button"
            data-testid={`${testIdPrefix}-filters-drawer-close-button`}
            onClick={onClose}
            className="-mr-2 rounded-full p-2 text-slate-400 transition-all hover:bg-slate-50 hover:text-slate-600"
            aria-label="Close filters"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div
          data-testid={`${testIdPrefix}-filters-drawer-content`}
          className={cn(
            'flex-1 space-y-6 overflow-y-auto p-6',
            styles.fields,
          )}
        >
          {children}
        </div>

        <div
          data-testid={`${testIdPrefix}-filters-drawer-footer`}
          className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 bg-slate-50 p-4 sm:p-6"
        >
          <button
            type="button"
            data-testid={`${testIdPrefix}-filters-reset-all-button`}
            onClick={onReset}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
          >
            {resetLabel}
          </button>
          <button
            type="button"
            data-testid={`${testIdPrefix}-filters-done-button`}
            onClick={() => {
              onDone?.()
              onClose()
            }}
            className="flex-1 rounded-lg bg-[rgb(21_151_255)] py-2.5 text-sm font-semibold text-white shadow-md shadow-[rgba(21,151,255,0.22)] transition-all hover:bg-[#0d7acc]"
          >
            {doneLabel}
          </button>
        </div>
      </aside>
    </>
  )

  return createPortal(content, document.body)
}
